import { useEffect, useRef, useState } from "react";

import useImmediateEffect from "@/hooks/useImmediateEffect";
import evaluateRestQuery, {
  type EvaluateRestQueryResponse,
} from "../evaluateRestQuery/index";

interface Options {
  skip?: boolean;
}

export type UseRestQueryResponse<APIResponse> = { loading: boolean } & Partial<
  Awaited<EvaluateRestQueryResponse<APIResponse>>
>;

/**
 * Handle the state of a REST query from a react component.
 * @param {(Request|string|URL)} resource - the resource (request or URL) to
 *  query for
 * @param {Object} options - configurations for the REST query
 * @param {boolean} options.skip - whether or not to skip executing the query,
 *  which can be helpful because hooks should not be called conditionally, and
 *  instead this skip option can be conditionally toggled
 *
 * returns {{data: Object, error: Error, loading: boolean}} the query state
 */
const useRestQuery = <T>(
  resource: RequestInfo | URL,
  { skip = false }: Options = {},
): UseRestQueryResponse<T> => {
  const [queryState, setQueryState] = useState({
    loading: !skip,
  });
  const setQueryLoading = (bool: boolean) => {
    setQueryState((current) => {
      // If we're already in that loading state, don't change.
      if (current.loading === bool) return current;
      return {
        ...current,
        loading: bool,
      };
    });
  };
  // Keep track of whether we've (yet) executed the current query.
  const hasExecutedThisQueryRef = useRef(false);
  useImmediateEffect(() => {
    hasExecutedThisQueryRef.current = false;
  }, [resource]);
  // When the resource changes, re-evaluate the query.
  useEffect(() => {
    // Do not execute the query if `skip` is toggled on.
    if (skip) return;
    // Do not execute the query again if we've already executed it.
    if (hasExecutedThisQueryRef.current) return;
    let queryCanceled = false;
    const load = async () => {
      hasExecutedThisQueryRef.current = true;
      setQueryLoading(true);
      const newQueryResult = await evaluateRestQuery<T>(resource);
      // If the query was canceled, avoid state change.
      if (queryCanceled) return;
      setQueryState({
        ...newQueryResult,
        loading: false,
      });
    };
    load().catch(console.error);
    return () => {
      // The query cancels when the resource changes, or on unmount.
      queryCanceled = true;
    };
  }, [resource, skip]);
  return queryState;
};

export default useRestQuery;
